/**
 * Draw Engine types — Event-scoped draws (ADR-031 P3).
 * Persisted under arenas/{arenaId}/events/{eventId}/draws + fixtures.
 * Formats: round_robin | single_elimination only (MVP).
 */

/**
 * @typedef {'round_robin'|'single_elimination'} DrawFormat
 * @typedef {'draft'|'generated'|'locked'|'completed'} DrawStatus
 * @typedef {'pending'|'bye'|'completed'|'done'} DrawFixtureStatus
 */

/**
 * @typedef {Object} DrawFixture
 * @property {string} fixtureId
 * @property {number} roundIndex
 * @property {string} roundName
 * @property {number} matchIndex
 * @property {string | null} court
 * @property {number | null} scheduledTimeMs
 * @property {string | null} entryAId
 * @property {string | null} entryBId
 * @property {string | null} labelA
 * @property {string | null} labelB
 * @property {DrawFixtureStatus | string} status
 * @property {string | null} winnerEntryId
 * @property {number | null} scoreA
 * @property {number | null} scoreB
 * @property {unknown[]} scores
 * @property {{ round: number, match: number }} path
 * @property {boolean} [isBye]
 * @property {number | null} [matchNo]
 */

/**
 * @typedef {Object} ManagedDraw
 * @property {string} drawId
 * @property {string} competitionId
 * @property {string} eventId
 * @property {DrawFormat} format
 * @property {DrawStatus} status
 * @property {string[]} participantEntryIds
 * @property {DrawFixture[]} fixtures
 * @property {object} structure  gameLogic-shaped rounds for visualization
 * @property {number} participantCount
 * @property {number} roundCount
 * @property {number} matchCount
 * @property {number} byeCount
 * @property {number | null} [generationSeed]
 * @property {number} generatedAtMs
 * @property {string} createdBy
 * @property {number} [lockedAtMs]
 * @property {number} [completedAtMs]
 */

export const DRAW_FORMATS = Object.freeze({
  ROUND_ROBIN: "round_robin",
  SINGLE_ELIMINATION: "single_elimination",
});

export const DRAW_FORMAT_LABELS = Object.freeze({
  round_robin: "Round Robin",
  single_elimination: "Single Elimination",
});

export const DRAW_STATUSES = Object.freeze({
  DRAFT: "draft",
  GENERATED: "generated",
  LOCKED: "locked",
  COMPLETED: "completed",
});

export const DRAW_STATUS_LABELS = Object.freeze({
  draft: "Draft",
  generated: "Generated",
  locked: "Locked",
  completed: "Completed",
});

export function newDrawId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `drw_${crypto.randomUUID()}`;
  }
  return `drw_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function newFixtureId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `fx_${crypto.randomUUID()}`;
  }
  return `fx_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Deterministic PRNG (mulberry32).
 * @param {number} seed
 */
export function createSeededRandom(seed) {
  let t = seed >>> 0;
  return function next() {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * @template T
 * @param {T[]} list
 * @param {number} [seed]
 * @returns {T[]}
 */
export function seededShuffle(list, seed) {
  const r = [...list];
  const rand = seed == null ? Math.random : createSeededRandom(Number(seed) || 1);
  for (let i = r.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
}
